import React, { Component } from "react";
import PropTypes from "prop-types";

import { isDefined, isNotDefined, noop } from "../utils";

import {
  getValueFromOverride,
  terminate,
  saveNodeType,
  isHoverForInteractiveType,
  mergeAppearance,
  resolveStroke,
} from "./utils";

import EachSegment from "./wrapper/EachSegment";
import StraightLine from "./components/StraightLine";
import MouseLocationIndicator from "./components/MouseLocationIndicator";
import HoverTextNearMouse from "./components/HoverTextNearMouse";
import { addEventListenerAllWindows } from "../../../utils/popoutWindowsRegistry";

/**
 * Continuous line drawing tool.
 * Creates a series of connected line segments, each one completed with a user click.
 * Each segment is stored as an independent object in the segments array.
 * Double-click to finish drawing.
 */
class Continuous extends Component {
  constructor(props) {
    super(props);

    this.handleStart = this.handleStart.bind(this);
    this.handleEnd = this.handleEnd.bind(this);
    this.handleDrawLine = this.handleDrawLine.bind(this);
    this.handleDragLine = this.handleDragLine.bind(this);
    this.handleDragLineComplete = this.handleDragLineComplete.bind(this);
    this.handleDoubleClick = this.handleDoubleClick.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);

    this.terminate = terminate.bind(this);
    this.saveNodeType = saveNodeType.bind(this);

    this.getSelectionState = isHoverForInteractiveType("segments").bind(this);

    this.state = {
      current: null,
      override: null,
    };
    this.nodes = [];
    this.lastClickTime = 0;
  }

  componentDidMount() {
    // PoC #popout: attach in every window — keys land on the focused window.
    this._detachKeydown = addEventListenerAllWindows(
      "keydown",
      this.handleKeyDown,
    );
  }

  componentWillUnmount() {
    this._detachKeydown?.();
  }

  componentDidUpdate(prevProps) {
    // Reset state when tool is re-enabled (start fresh, not connected to previous segments)
    if (this.props.enabled && !prevProps.enabled) {
      this.setState({
        current: null,
        override: null,
      });
      this.lastClickTime = 0;
    }
  }

  handleKeyDown(e) {
    if (e.key === "Escape" && this.props.enabled) {
      // Cancel current segment (don't add it) and exit drawing mode
      this.setState(
        {
          current: null,
        },
        () => {
          if (typeof this.props.onFinish === "function") {
            this.props.onFinish();
          }
        },
      );
    }
  }

  handleDragLine(index, newXYValue) {
    this.setState({
      override: {
        index,
        ...newXYValue,
      },
    });
  }

  handleDragLineComplete(moreProps) {
    const { override } = this.state;
    if (isDefined(override)) {
      const { segments } = this.props;
      const newSegments = segments.map((each, idx) =>
        idx === override.index
          ? {
              ...each,
              start: [override.x1Value, override.y1Value],
              end: [override.x2Value, override.y2Value],
              selected: true,
            }
          : {
              ...each,
              selected: false,
            },
      );

      this.setState(
        {
          override: null,
        },
        () => {
          this.props.onComplete(newSegments, moreProps);
        },
      );
    }
  }

  handleDrawLine(xyValue) {
    const { current } = this.state;
    if (isDefined(current) && isDefined(current.start)) {
      this.mouseMoved = true;
      this.setState({
        current: {
          start: current.start,
          end: xyValue,
        },
      });
    }
  }

  handleStart(xyValue, moreProps, e) {
    const { current } = this.state;

    // Only start if not already drawing
    if (isNotDefined(current) || isNotDefined(current.start)) {
      this.mouseMoved = false;

      this.setState(
        {
          current: {
            start: xyValue,
            end: null,
          },
        },
        () => {
          this.props.onStart(moreProps, e);
        },
      );
    }
  }

  handleDoubleClick() {
    // Finish continuous drawing
    this.setState(
      {
        current: null,
      },
      () => {
        // Notify parent to exit drawing mode
        if (typeof this.props.onFinish === "function") {
          this.props.onFinish();
        }
      },
    );
  }

  handleEnd(xyValue, moreProps, e) {
    const { current } = this.state;
    const { segments, appearance, type } = this.props;

    // Check for double-click to finish
    const now = Date.now();
    if (now - this.lastClickTime < 300) {
      this.handleDoubleClick();
      this.lastClickTime = 0;
      return;
    }
    this.lastClickTime = now;

    // If we have a valid segment being drawn
    if (this.mouseMoved && isDefined(current) && isDefined(current.start)) {
      const currentAppearance = appearance;

      // Create new segment
      const newSegment = {
        start: current.start,
        end: xyValue,
        selected: false,
        appearance: currentAppearance,
        type,
      };

      // Add to segments array
      const newSegments = [
        ...segments.map((d) => ({ ...d, selected: false })),
        newSegment,
      ];

      // KEY DIFFERENCE from Segment.jsx:
      // Don't reset current to null - continue from end point
      this.setState(
        {
          current: {
            start: xyValue, // End point becomes new start
            end: null,
          },
        },
        () => {
          this.props.onComplete(newSegments, moreProps, e);
        },
      );

      // Reset mouseMoved for next segment
      this.mouseMoved = false;
    }
  }

  render() {
    const { appearance } = this.props;
    const { enabled, snap, shouldDisableSnap, snapTo, type } = this.props;
    const { currentPositionRadius, currentPositionStroke } = this.props;
    const { currentPositionstrokeOpacity, currentPositionStrokeWidth } =
      this.props;
    const { hoverText, segments } = this.props;
    const { current, override } = this.state;

    const currentStroke = resolveStroke(appearance);
    const tempLine =
      isDefined(current) && isDefined(current.end) ? (
        <StraightLine
          type={type}
          noHover
          x1Value={current.start[0]}
          y1Value={current.start[1]}
          x2Value={current.end[0]}
          y2Value={current.end[1]}
          stroke={currentStroke.stroke}
          strokeWidth={currentStroke.strokeWidth}
          strokeOpacity={currentStroke.strokeOpacity}
        />
      ) : null;

    return (
      <g>
        {segments.map((each, idx) => {
          const eachAppearance = mergeAppearance(appearance, each.appearance);
          const eachStroke = resolveStroke(eachAppearance);

          const hoverTextWithDefault = {
            ...Continuous.defaultProps.hoverText,
            ...hoverText,
          };

          return (
            <EachSegment
              key={idx}
              ref={this.saveNodeType(idx)}
              index={idx}
              type={each.type}
              selected={each.selected}
              x1Value={getValueFromOverride(
                override,
                idx,
                "x1Value",
                each.start[0],
              )}
              y1Value={getValueFromOverride(
                override,
                idx,
                "y1Value",
                each.start[1],
              )}
              x2Value={getValueFromOverride(
                override,
                idx,
                "x2Value",
                each.end[0],
              )}
              y2Value={getValueFromOverride(
                override,
                idx,
                "y2Value",
                each.end[1],
              )}
              stroke={eachStroke.stroke}
              strokeWidth={eachStroke.strokeWidth}
              strokeOpacity={eachStroke.strokeOpacity}
              strokeDasharray={eachStroke.strokeDasharray}
              edgeStroke={eachAppearance.edgeStroke}
              edgeFill={eachAppearance.edgeFill}
              r={eachAppearance.r}
              hoverText={hoverTextWithDefault}
              onDrag={this.handleDragLine}
              onDragComplete={this.handleDragLineComplete}
              edgeInteractiveCursor="react-stockcharts-move-cursor"
              lineInteractiveCursor="react-stockcharts-move-cursor"
            />
          );
        })}
        {tempLine}
        <MouseLocationIndicator
          enabled={enabled}
          snap={snap}
          shouldDisableSnap={shouldDisableSnap}
          snapTo={snapTo}
          r={currentPositionRadius}
          stroke={currentPositionStroke}
          strokeOpacity={currentPositionstrokeOpacity}
          strokeWidth={currentPositionStrokeWidth}
          onMouseDown={this.handleStart}
          onClick={this.handleEnd}
          onMouseMove={this.handleDrawLine}
          data-evo-id="interactive-continuous"
        />
      </g>
    );
  }
}

Continuous.propTypes = {
  snap: PropTypes.bool.isRequired,
  enabled: PropTypes.bool.isRequired,
  snapTo: PropTypes.func,
  shouldDisableSnap: PropTypes.func.isRequired,

  onStart: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
  onFinish: PropTypes.func, // Called when user double-clicks to exit drawing mode
  onSelect: PropTypes.func,

  currentPositionStroke: PropTypes.string,
  currentPositionStrokeWidth: PropTypes.number,
  currentPositionstrokeOpacity: PropTypes.number,
  currentPositionRadius: PropTypes.number,
  type: PropTypes.oneOf(["LINE"]),
  hoverText: PropTypes.object.isRequired,

  segments: PropTypes.array.isRequired,

  appearance: PropTypes.shape({
    stroke: PropTypes.oneOfType([
      PropTypes.shape({
        color: PropTypes.string,
        dash: PropTypes.string,
        width: PropTypes.number,
        opacity: PropTypes.number,
      }),
      PropTypes.string,
    ]),
    edgeStroke: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        color: PropTypes.string,
        dash: PropTypes.string,
        width: PropTypes.number,
        opacity: PropTypes.number,
      }),
    ]),
    edgeFill: PropTypes.string,
  }).isRequired,
};

Continuous.defaultProps = {
  type: "LINE",

  onStart: noop,
  onComplete: noop,
  onSelect: noop,

  currentPositionStroke: "#000000",
  currentPositionstrokeOpacity: 1,
  currentPositionStrokeWidth: 3,
  currentPositionRadius: 0,

  shouldDisableSnap: (e) => e.button === 2 || e.shiftKey,
  snap: false,
  hoverText: {
    ...HoverTextNearMouse.defaultProps,
    enable: false,
    bgHeight: "auto",
    bgWidth: "auto",
    text: "Clique para selecionar",
    selectedText: "",
  },
  segments: [],

  appearance: {
    stroke: {
      color: "#000000",
      width: 1,
      opacity: 1,
      dash: "Solid",
    },
    edgeStroke: { color: "#000000", dash: "Solid", width: 1, opacity: 1 },
    edgeFill: "#FFFFFF",
    r: 6,
  },
};

export default Continuous;
